import { useEffect, useRef, useState } from 'react';
import { Laptop, Smartphone, Tablet, Mail, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { getRecentOpens } from '@/api/tracking';
import type { RecentOpen } from '@/types/email';

interface RecentOpensFeedProps {
  accountId: string;
  limit?: number;
  className?: string;
  inDialog?: boolean;
}

function getDeviceIcon(deviceType: string, className?: string) {
  const iconClass = cn('h-3.5 w-3.5', className);
  switch (deviceType) {
    case 'mobile':
      return <Smartphone className={iconClass} />;
    case 'tablet':
      return <Tablet className={iconClass} />;
    case 'desktop':
      return <Laptop className={iconClass} />;
    default:
      return <Mail className={iconClass} />;
  }
}

function formatTimeAgo(dateString: string) {
  try {
    const date = new Date(dateString);
    return formatDistanceToNow(date, { addSuffix: true });
  } catch {
    return dateString;
  }
}

export function RecentOpensFeed({ accountId, limit = 50, className, inDialog }: RecentOpensFeedProps) {
  const [opens, setOpens] = useState<RecentOpen[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sequence guard (X10): an account switch / double-refresh must not let a
  // slower response win and show the wrong account's opens.
  const requestSeqRef = useRef(0);

  const fetchOpens = async () => {
    const seq = ++requestSeqRef.current;
    setIsLoading(true);
    setError(null);
    try {
      const data = await getRecentOpens(accountId, limit);
      if (seq !== requestSeqRef.current) return;
      setOpens(data);
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error('Failed to fetch recent opens:', err);
      setError('Failed to load recent opens');
    } finally {
      if (seq === requestSeqRef.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accountId) {
      fetchOpens();
    }
  }, [accountId, limit]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className={cn('p-8 text-center', className)}>
        <RefreshCw className="h-5 w-5 animate-spin mx-auto text-muted-foreground/50" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('p-4 text-center', className)}>
        <p className="text-xs text-destructive">{error}</p>
        <Button onClick={fetchOpens} variant="ghost" size="sm" className="mt-2 h-6 text-xs">
          Retry
        </Button>
      </div>
    );
  }

  if (opens.length === 0) {
    return (
      <div className={cn('p-8 text-center', className)}>
        <p className="text-sm text-muted-foreground">No recent opens</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col', className)}>
      <div className={cn(
        "px-4 py-2 border-b flex items-center justify-between bg-muted/10",
        inDialog && "py-4.5 pr-14"
      )}>
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Recent Opens</h3>
        <Button onClick={fetchOpens} variant="ghost" size="sm" className="h-6 w-6 p-0 hover:bg-transparent">
          <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
        </Button>
      </div>

      <div className="flex-1 max-h-[400px] overflow-y-auto">
        <div className="py-1">
          {opens.map((open) => {
            const recipientName = open.inferredRecipient ||
              (Array.isArray(open.toAddresses) && open.toAddresses.length > 0
                ? open.toAddresses[0].name || open.toAddresses[0].email
                : 'Someone');

            const location = open.locationCity
              ? `${open.locationCity}${open.locationCountry ? `, ${open.locationCountry}` : ''}`
              : null;

            return (
              <div
                key={open.id}
                className="px-4 py-2.5 hover:bg-accent/50 transition-colors cursor-pointer border-b border-border/40 last:border-0 group"
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-sm font-medium text-foreground truncate max-w-[180px]">
                    {recipientName}
                  </span>
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap ml-2">
                    {formatTimeAgo(open.openedAt)}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 text-xs text-muted-foreground/80">
                  {getDeviceIcon(open.deviceType, "text-muted-foreground/70")}
                  <span className="truncate max-w-[200px]">
                    {open.subject || 'No subject'}
                  </span>
                </div>
                
                {(location || open.deviceName) && (
                  <div className="mt-1 flex items-center gap-1.5 text-[10px] text-muted-foreground/60">
                    {location && <span>{location}</span>}
                    {location && open.deviceName && <span>•</span>}
                    {open.deviceName && <span>{open.deviceName}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
